import React from 'react'

import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'

import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  label: string
  propertyKey: string
  typeLabel: string
  // System fields are always included and non-removable; `required` only toggles
  // the mandatory asterisk / switch (Name is required, icon/color/parent are not).
  required?: boolean
  isExpanded: boolean
  onToggleExpand: () => void
}

export const SystemPropertyRow: React.FC<IProps> = ({
  label,
  propertyKey,
  typeLabel,
  required = false,
  isExpanded,
  onToggleExpand,
}) => {
  return (
    <div
      className="tw:flex tw:select-none"
      aria-disabled="true"
      data-system="true"
    >
      {/* Drag-handle slot — matches the 40px placeholder width of user rows so the row content aligns */}
      <div className="tw:w-10 tw:shrink-0" />

      <div className="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:border-0 tw:border-b tw:border-solid tw:border-border">
        <div
          data-testid="row-header"
          className="tw:flex tw:items-center tw:px-4 tw:py-2 tw:gap-3 tw:min-h-[42px] tw:cursor-pointer tw:bg-muted"
          onClick={onToggleExpand}
        >
          <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Checkbox checked disabled />
          </div>
          {isExpanded ? (
            <ChevronDownIcon
              size={16}
              className="tw:shrink-0 tw:text-muted-foreground"
            />
          ) : (
            <ChevronRightIcon
              size={16}
              className="tw:shrink-0 tw:text-muted-foreground"
            />
          )}
          <div className="tw:flex-1 tw:min-w-0 tw:break-words tw:text-label-lg tw:text-foreground">
            {label}
            {required && (
              <span
                data-testid="mandatory-asterisk"
                className="tw:text-destructive tw:ml-1"
              >
                *
              </span>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="tw:px-3 tw:bg-background">
            <div
              data-testid="property-detail-row"
              className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:py-2"
            >
              <span className="tw:shrink-0 tw:text-label-lg tw:text-muted-foreground">
                {Localization.translator.name}
              </span>
              <span className="tw:min-w-0 tw:break-words tw:text-right tw:text-body-md tw:text-foreground tw:font-mono">
                {propertyKey}
              </span>
            </div>
            <div
              data-testid="property-detail-row"
              className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:py-2"
            >
              <span className="tw:shrink-0 tw:text-label-lg tw:text-muted-foreground">
                {Localization.translator.type}
              </span>
              <span className="tw:min-w-0 tw:break-words tw:text-right tw:text-body-md tw:text-foreground">
                {typeLabel}
              </span>
            </div>
            <div
              data-testid="property-detail-row"
              className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:py-2"
            >
              <span className="tw:min-w-0 tw:break-words tw:text-label-lg tw:text-muted-foreground">
                {Localization.translator.mandatory}
              </span>
              <Switch className="tw:shrink-0" checked={required} disabled />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
